import { useState } from "react";
import { motion, AnimatePresence, ease as mmEase } from "@/lib/motion";
import { Pencil, Mail, Phone, MapPin, ShieldCheck, BadgeCheck, CheckCircle2, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { COUNTRIES } from "@/lib/constants";
import { initials, formatDate } from "@/lib/format";

const ez = mmEase.expo;

const AVATAR_COLORS = ["#10B981", "#7C3AED", "#06B6D4", "#F59E0B", "#EF4444", "#0B1D3A"];

export default function Profile() {
  const { user, refresh } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [form, setForm] = useState({
    full_name: user?.full_name || "",
    phone:     user?.phone     || "",
    country:   user?.country   || "GB",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/me", form);
      toast.success("Profile updated");
      setEditing(false);
      refresh();
    } catch {
      toast.error("Could not update profile");
    } finally {
      setSaving(false);
    }
  };

  const avatarColor = user?.avatar_color || AVATAR_COLORS[0];
  const country = COUNTRIES.find((c) => c.code === user?.country);

  return (
    <div className="space-y-6 max-w-3xl">

      {/* ── Header ──────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: -12, filter: "blur(4px)" }}
        animate={{ opacity: 1, y: 0,   filter: "blur(0px)" }}
        transition={{ duration: 0.45, ease: ez }}
      >
        <p className="text-[10px] uppercase tracking-[0.22em] text-emerald font-bold mb-1">Account</p>
        <h1 className="font-display text-[1.65rem] font-black text-navy tracking-tight">Your profile</h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          Keep your details current — it keeps your transfers smooth.
        </p>
      </motion.div>

      {/* ── Profile hero card ───────────────────────────────────── */}
      <motion.div
        className="card-elevated overflow-hidden"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.44, ease: ez, delay: 0.06 }}
      >
        <div className="brand-gradient px-7 pt-7 pb-6 relative overflow-hidden">
          <div
            className="absolute -top-16 -right-16 w-48 h-48 rounded-full blur-3xl pointer-events-none"
            style={{ background: `${avatarColor}28` }}
          />
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
          <div className="relative flex items-start gap-5">
            {/* Avatar */}
            <div
              className="w-20 h-20 rounded-3xl grid place-items-center text-white text-2xl font-display font-black shrink-0"
              style={{
                background: avatarColor,
                boxShadow: `0 8px 24px -6px ${avatarColor}66`,
              }}
            >
              {initials(user?.full_name || "ME")}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2.5 flex-wrap mb-1">
                <h2 className="font-display text-2xl font-black text-white">{user?.full_name}</h2>
                {user?.kyc_status === "approved" && (
                  <span className="badge-emerald shrink-0">
                    <BadgeCheck size={9} /> Verified
                  </span>
                )}
              </div>
              <p className="text-white/45 text-sm">{user?.email}</p>
              <p className="text-white/30 text-[11px] mt-1">
                Member since {user?.created_at ? formatDate(user.created_at).split(",")[0] : "—"}
              </p>
            </div>
            <button
              data-testid="profile-edit"
              onClick={() => setEditing(true)}
              className="shrink-0 flex items-center gap-1.5 text-[12px] font-bold bg-white/10 hover:bg-white/20 text-white/80 hover:text-white px-3.5 py-2 rounded-full transition-all"
            >
              <Pencil size={12} /> Edit
            </button>
          </div>
        </div>

        <div className="p-6 grid sm:grid-cols-2 gap-3">
          <InfoItem icon={Mail}      label="Email"   value={user?.email}   verified={user?.email_verified} />
          <InfoItem icon={Phone}     label="Phone"   value={user?.phone}   verified={user?.phone_verified} />
          <InfoItem icon={MapPin}    label="Country" value={country ? `${country.flag} ${country.name}` : user?.country} />
          <InfoItem icon={ShieldCheck} label="KYC"  value={user?.kyc_status} capitalize />
        </div>
      </motion.div>

      {/* ── Edit form ───────────────────────────────────────────── */}
      <AnimatePresence>
        {editing && (
          <motion.div
            className="card-elevated p-6"
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0,  scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.3, ease: ez }}
          >
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="font-display text-lg font-bold text-navy">Edit profile</h3>
                <p className="text-[12px] text-muted-foreground mt-0.5">Changes apply immediately to your account.</p>
              </div>
              <button
                onClick={() => setEditing(false)}
                className="w-8 h-8 rounded-xl grid place-items-center text-muted-foreground hover:text-navy hover:bg-secondary transition-all"
              >
                <X size={15} />
              </button>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="input-label" htmlFor="p-name">Full name</label>
                <input
                  id="p-name"
                  data-testid="profile-name"
                  value={form.full_name}
                  onChange={set("full_name")}
                  className="input-field mt-1.5 w-full"
                />
              </div>
              <div>
                <label className="input-label" htmlFor="p-phone">Phone</label>
                <input
                  id="p-phone"
                  data-testid="profile-phone"
                  value={form.phone}
                  onChange={set("phone")}
                  className="input-field mt-1.5 w-full"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="input-label" htmlFor="p-country">Country</label>
                <select
                  id="p-country"
                  data-testid="profile-country"
                  value={form.country}
                  onChange={set("country")}
                  className="input-field mt-1.5 w-full text-navy appearance-none cursor-pointer"
                >
                  {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.flag} {c.name}</option>)}
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-5">
              <button data-testid="profile-cancel" onClick={() => setEditing(false)} className="btn-ghost-dark">
                Cancel
              </button>
              <button
                data-testid="profile-save"
                onClick={save}
                disabled={saving}
                className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {saving
                  ? <><Loader2 size={13} className="animate-spin" /> Saving…</>
                  : <><CheckCircle2 size={13} /> Save changes</>}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function InfoItem({ icon: Icon, label, value, verified, capitalize }) {
  return (
    <div className="bg-secondary/60 rounded-2xl p-4 flex items-start gap-3">
      <div className="w-9 h-9 rounded-xl bg-white grid place-items-center text-emerald shrink-0"
        style={{ boxShadow: "var(--shadow-xs)" }}>
        <Icon size={14} strokeWidth={1.8} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-0.5">{label}</p>
        <p className={`font-semibold text-navy text-sm truncate ${capitalize ? "capitalize" : ""}`}>
          {value || "—"}
        </p>
        {typeof verified === "boolean" && (
          <p className={`text-[10px] mt-0.5 font-bold ${verified ? "text-emerald" : "text-muted-foreground/70"}`}>
            {verified ? "Verified" : "Not verified"}
          </p>
        )}
      </div>
    </div>
  );
}
